import {SupportAttachments} from "./SupportAttachments";
import {SupportCompose} from "./SupportCompose";
import {SupportConversation} from "./SupportConversation";
import { CustomerError } from "../../ui/CustomerError";
import {
  GlassChip,
  BlobCtaButton,
  Typography,
} from "@cupcodev/ui";
import { useEffect, useState } from "react";
import { CtaLink, EmptyState, Facts, FilterTabs, NaveIcon, Notice, PageHeader, PageSkeleton, Panel, RowIcon, RowLink, RowList } from "../../ui/NavePage";
import { Link, useParams } from "react-router-dom";
import { isCustomerDependencyErrorCode } from "../../application/foundation/customer-read-port-error";
import type {
  CustomerSupportTicketDetail,
  CustomerSupportTicketList,
  CustomerSupportTicketReadModel,
} from "../../read-models/support/customer-support-read-model";
import { useI18n } from "../i18n/I18nProvider";
import { useScenario } from "../scenario/ScenarioProvider";

type QueryError = Readonly<{ code: string }>;
type QueryResult<T> = Readonly<{ ok: true; value: T }> | Readonly<{ ok: false; error: QueryError }>;
type LoadState<T> =
  | Readonly<{ kind: "loading" }>
  | Readonly<{ kind: "ready"; value: T }>
  | Readonly<{ kind: "error"; code: string }>;

const useLoad = <T,>(loadKey: string, load: () => Promise<QueryResult<T>>, refreshKey = 0): LoadState<T> => {
  const [state, setState] = useState<LoadState<T>>({ kind: "loading" });

  // A refresh re-reads in the background: the screen keeps what it shows (and its scroll) until the new data arrives.
  useEffect(() => {
    if (refreshKey === 0) return;
    let cancelled = false;
    void load().then((result) => { if (!cancelled && result.ok) setState({ kind: "ready", value: result.value }); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [refreshKey]);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    void load()
      .then((result) => {
        if (cancelled) return;
        setState(result.ok
          ? { kind: "ready", value: result.value }
          : { kind: "error", code: result.error.code });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: "error", code: "UNEXPECTED" });
      });
    return () => { cancelled = true; };
  }, [loadKey]);

  return state;
};

const LoadingState = ({ label }: { label: string }) => <PageSkeleton label={label} layout="list" />;

const ErrorState = ({ code, inline = false }: { code: string; inline?: boolean }) => {
  const { t } = useI18n();
  const dependency = isCustomerDependencyErrorCode(code);
  const forbidden = code === "FORBIDDEN";
  const notFound = code === "NOT_FOUND";
  const titleKey = dependency
    ? "support.errors.unavailable.title"
    : forbidden
      ? "support.errors.forbidden.title"
      : notFound
        ? "support.errors.notFound.title"
        : "support.errors.generic.title";
  const bodyKey = dependency
    ? "support.errors.unavailable.body"
    : forbidden
      ? "support.errors.forbidden.body"
      : notFound
        ? "support.errors.notFound.body"
        : "support.errors.generic.body";
  return <CustomerError inline={inline} title={t(titleKey)} description={t(bodyKey)} />;
};

const supportTone = (status: CustomerSupportTicketReadModel["customerStatus"]) =>
  status === "WAITING_CLIENT" ? "purple" as const : status === "RESOLVED" || status === "CLOSED" ? "success" as const : "neutral" as const;

const SupportStatus = ({ status }: { status: CustomerSupportTicketReadModel["customerStatus"] }) => {
  const { t } = useI18n();
  return <GlassChip variant={supportTone(status)}>{t(`support.status.${status}`)}</GlassChip>;
};

const TicketMetadata = ({ ticket }: { ticket: CustomerSupportTicketReadModel }) => {
  const { t, formatDate } = useI18n();
  return (
    <Facts items={[
      { label: t("support.lastActivity"), value: formatDate(ticket.lastActivityAt), icon: "lucide:clock" },
      ticket.awaitingActor === "NONE" ? null : { label: t("support.awaiting"), value: t(`support.awaiting.${ticket.awaitingActor}`), icon: "lucide:hourglass" },
      ticket.serviceRef === undefined ? null : { label: t("support.serviceRef"), value: ticket.serviceRef, icon: "lucide:layers" },
      ticket.unreadCount === undefined ? null : { label: t("support.unread"), value: String(ticket.unreadCount), icon: "lucide:mail" },
    ]} />
  );
};

type TicketFilter = "all" | "waiting" | "active" | "done";
const isDone = (status: string): boolean => status === "RESOLVED" || status === "CLOSED";

export const SupportPage = () => {
  const { runtime, activeOrganizationId } = useScenario();
  const { t } = useI18n();
  const key = `${runtime.scenario.id}:${activeOrganizationId.value}:support`;
  const tickets = useLoad<CustomerSupportTicketList>(key, () => runtime.experience.listSupportTickets());

  if (tickets.kind === "loading") return <LoadingState label={t("state.loading")} />;
  if (tickets.kind === "error") return <ErrorState code={tickets.code} />;

  return (
    <SupportReadyPage
      loadKey={key}
      initial={tickets.value}
      loadNext={(cursor) => runtime.experience.listSupportTickets({ cursor })}
    />
  );
};

const SupportReadyPage = ({
  initial,
  loadKey,
  loadNext,
}: {
  initial: CustomerSupportTicketList;
  loadKey: string;
  loadNext(cursor: string): Promise<QueryResult<CustomerSupportTicketList>>;
}) => {
  const { t, formatDate } = useI18n();
  const [items, setItems] = useState<readonly CustomerSupportTicketReadModel[]>(initial.items);
  const [nextCursor, setNextCursor] = useState<string | undefined>(initial.nextCursor);
  const [hasMore, setHasMore] = useState(initial.hasMore);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | undefined>(undefined);

  useEffect(() => {
    setItems(initial.items);
    setNextCursor(initial.nextCursor);
    setHasMore(initial.hasMore);
    setLoadingMore(false);
    setLoadMoreError(undefined);
  }, [loadKey, initial]);

  const [filter, setFilter] = useState<TicketFilter>("all");
  const waiting = items.filter((ticket) => ticket.customerStatus === "WAITING_CLIENT");
  const active = items.filter((ticket) => !isDone(ticket.customerStatus) && ticket.customerStatus !== "WAITING_CLIENT");
  const done = items.filter((ticket) => isDone(ticket.customerStatus));
  const visible = filter === "waiting" ? waiting : filter === "active" ? active : filter === "done" ? done : items;

  const loadMore = async (): Promise<void> => {
    if (loadingMore || nextCursor === undefined) return;
    setLoadingMore(true);
    setLoadMoreError(undefined);
    try {
      const result = await loadNext(nextCursor);
      if (!result.ok) {
        setLoadMoreError(result.error.code);
        return;
      }
      const byId = new Map(items.map((item) => [item.ticketId, item]));
      for (const item of result.value.items) byId.set(item.ticketId, item);
      setItems([...byId.values()]);
      setNextCursor(result.value.nextCursor);
      setHasMore(result.value.hasMore);
    } catch {
      setLoadMoreError("UNEXPECTED");
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="nave-page support-experience-page">
      <PageHeader
        icon="lucide:life-buoy"
        title={t("support.page.title")}
        description={t("support.page.subtitle")}
      />

      {waiting.length > 0 ? (
        <Link className="attention-item support-waiting" to={`/support/tickets/${waiting[0]!.ticketId}`}>
          <span className="attention-item-top">
            <span className="nave-meta-item"><NaveIcon name="lucide:bell-ring" />{t("support.summary.title")}</span>
            <SupportStatus status="WAITING_CLIENT" />
          </span>
          <span className="attention-item-title">{waiting[0]!.subject}</span>
          <span className="attention-item-foot"><span className="nave-meta-item"><NaveIcon name="lucide:clock" />{formatDate(waiting[0]!.lastActivityAt)}</span><span className="attention-item-cta">{t("support.ticket.view")}<NaveIcon name="lucide:arrow-right" /></span></span>
        </Link>
      ) : null}

      <div className="nave-layout">
        <div className="nave-layout-main">
          <Panel id="support-tickets-heading" icon="lucide:inbox" title={t("support.tickets.title")} {...(items.length ? { count: items.length } : {})}>
            {items.length === 0 ? (
              <EmptyState icon="lucide:inbox">{t("support.tickets.empty")}</EmptyState>
            ) : (
              <>
                <FilterTabs<TicketFilter> label={t("layout.filter")} value={filter} onChange={setFilter} options={[
                  { value: "all", label: t("layout.filter.all"), count: items.length },
                  { value: "waiting", label: t("support.status.WAITING_CLIENT"), count: waiting.length },
                  { value: "active", label: t("layout.filter.inProgress"), count: active.length },
                  { value: "done", label: t("layout.filter.done"), count: done.length },
                ]} />
                {visible.length === 0 ? <EmptyState icon="lucide:filter-x">{t("layout.noResults")}</EmptyState> : <RowList label={t("support.tickets.title")}>
                  {visible.map((ticket) => (
                    <li key={ticket.ticketId}>
                      <RowLink
                        href={`/support/tickets/${ticket.ticketId}`}
                        leading={<RowIcon name={isDone(ticket.customerStatus) ? "lucide:circle-check" : "lucide:message-circle"} tone={ticket.customerStatus === "WAITING_CLIENT" ? "alert" : isDone(ticket.customerStatus) ? "success" : "neutral"} />}
                        title={ticket.subject}
                        meta={<>
                          <span className="nave-meta-item"><NaveIcon name="lucide:clock" />{formatDate(ticket.lastActivityAt)}</span>
                          {ticket.awaitingActor === "NONE" ? null : <span className="nave-meta-item"><NaveIcon name="lucide:hourglass" />{t(`support.awaiting.${ticket.awaitingActor}`)}</span>}
                          {ticket.serviceRef === undefined ? null : <span className="nave-meta-item"><NaveIcon name="lucide:layers" />{ticket.serviceRef}</span>}
                        </>}
                        trailing={<>
                          {ticket.unreadCount !== undefined && ticket.unreadCount > 0 ? <span className="nave-count" aria-label={t("support.unread")}>{ticket.unreadCount}</span> : null}
                          <SupportStatus status={ticket.customerStatus} />
                        </>}
                      />
                    </li>
                  ))}
                </RowList>}
                {hasMore && nextCursor !== undefined ? (
                  <div className="nave-form-actions">
                    <BlobCtaButton type="button" size="sm" variant="secondary" disabled={loadingMore} loading={loadingMore} onClick={() => { void loadMore(); }}>
                      {t(loadingMore ? "support.tickets.loadingMore" : "support.tickets.loadMore")}
                    </BlobCtaButton>
                  </div>
                ) : null}
                {loadMoreError === undefined ? null : (
                  <Typography as="p" variant="caption" tone="muted" aria-live="polite">
                    {t("support.tickets.loadMoreError")}
                  </Typography>
                )}
              </>
            )}
          </Panel>
        </div>
        <aside className="nave-layout-aside" aria-label={t("layout.context")}>
          <SupportCompose key={loadKey} />
          <Panel icon="lucide:headset" title={t("support.channels")} tone="glass-sm">
            <div className="nave-actions-col">
              <CtaLink href="https://wa.me/5541996133902" external icon="lucide:message-circle-more" trailingIcon="lucide:arrow-up-right">{t("support.whatsapp")}</CtaLink>
              <CtaLink to="/help" icon="lucide:book-open" trailingIcon="lucide:arrow-right">{t("support.help")}</CtaLink>
            </div>
          </Panel>
        </aside>
      </div>
    </div>
  );
};

export const SupportTicketDetailPage = () => {
  const { ticketId = "" } = useParams();
  const { runtime, activeOrganizationId,session } = useScenario();
  const { t, formatDate } = useI18n();
  const [revision,setRevision]=useState(0);
  const [actionBusy,setActionBusy]=useState(false);const [actionError,setActionError]=useState(false);
  const state = useLoad<CustomerSupportTicketDetail>(
    `${runtime.scenario.id}:${activeOrganizationId.value}:support:${ticketId}`,
    () => runtime.experience.getSupportTicket(ticketId),
    revision,
  );
  // New replies from the team show up without leaving the page.
  const open = state.kind === "ready" && state.value.customerStatus !== "CLOSED";
  useEffect(() => {
    if (!open) return;
    const timer = window.setInterval(() => { if (!document.hidden) setRevision((v) => v + 1); }, 20_000);
    return () => window.clearInterval(timer);
  }, [open]);

  if (state.kind === "loading") return <LoadingState label={t("state.loading")} />;
  if (state.kind === "error") return <ErrorState code={state.code} />;

  const ticket = state.value;
  const canWriteSupport=session?.grants.some(g=>g.organizationId===activeOrganizationId.value&&g.capability==="support.reply"&&(g.scopeKind==="organization"&&g.scopeRef===activeOrganizationId.value||g.scopeKind==="resource"&&g.scopeRef===ticketId));
  const action=async(kind:"finalize"|"reopen")=>{if(actionBusy||!runtime.experience.submitSupport)return;setActionBusy(true);setActionError(false);try{const result=await runtime.experience.submitSupport({requestId:crypto.randomUUID(),text:"",action:kind},ticketId);if(result.ok)setRevision(v=>v+1);else setActionError(true);}catch{setActionError(true);}finally{setActionBusy(false);}};
  return (
    <div className="nave-page support-experience-page">
      <PageHeader
        crumbs={[{ label: t("navigation.support"), to: "/support" }, { label: ticket.subject }]}
        icon="lucide:message-circle"
        title={ticket.subject}
        status={{ label: t(`support.status.${ticket.customerStatus}`), tone: supportTone(ticket.customerStatus) }}
        meta={<span className="nave-meta-item"><NaveIcon name="lucide:clock" />{t("support.lastActivity")}: {formatDate(ticket.lastActivityAt)}</span>}
      />
      <div className="nave-layout">
        <div className="nave-layout-main">
          <SupportConversation key={`${runtime.scenario.id}:${ticketId}`} ticket={ticket} ticketId={ticketId} onSaved={() => setRevision(v => v + 1)} />
        </div>
        <aside className="nave-layout-aside" aria-label={t("layout.context")}>
          <Panel icon="lucide:info" title={t("support.ticket.summary")}>
            <TicketMetadata ticket={ticket} />
            {canWriteSupport && (ticket.canFinalize || ticket.canReopen) ? <div className="nave-actions-col">
              {ticket.canFinalize ? <BlobCtaButton disabled={actionBusy} loading={actionBusy} size="sm" onClick={() => void action("finalize")}>{t("support.finalize")}</BlobCtaButton> : null}
              {ticket.canReopen ? <BlobCtaButton disabled={actionBusy} loading={actionBusy} size="sm" variant="secondary" onClick={() => void action("reopen")}>{t("support.reopen")}</BlobCtaButton> : null}
            </div> : null}
            {actionError ? <Notice tone="warning">{t("support.actionError")}</Notice> : null}
          </Panel>
          <SupportAttachments key={`${runtime.scenario.id}:files:${ticketId}`} ticket={ticket} onSaved={() => setRevision(v => v + 1)} />
        </aside>
      </div>
    </div>
  );
};
